import {
  AgeOfPeopleEnum,
  AssetFamilySearchFilterEnum,
  AssetTypeSearchFilterEnum,
  IstockCollectionSearchFilterEnum,
  IstockExclusiveSearchFilterEnum,
  ColorMoodEnum,
  CompositionSearchFilterEnum,
  EditorialProductEnum,
  EthnicityEnum,
  FileTypeSearchFilterEnum,
  FilmDefinitionSearchFilterEnum,
  FrameRateEnum,
  ImageTechniqueEnum,
  LicenseSearchFilterEnum,
  MediaTypeSearchFilterEnum,
  NumberOfPeopleEnum,
  OrientationEnum,
  SortEnum,
  VideoTemplateAppVersionEnum,
  VideoTemplateCategoryEnum,
  RecencyEnum,
  AllowedUseSearchFilterEnum,
  PhraseProcessingSearchFilterEnum,
  OfflineContentSearchFilterEnum,
  ImageSizeSearchFilterEnum,
  ReleaseStatusSearchFilterEnum,
  ViewPointEnum,
} from '../filterValues';
import {
  SearchParameterFieldKind,
  type SearchParameterSchemaRow,
} from './rows';

/**
 * The single source of truth for every search parameter the app understands.
 *
 * Each row declares a parameter's `name`, its `kind`, optionally an `enum`
 * (for enum kinds), and optionally a `defaultValue`. The `SearchParameterValues`
 * type in `./derived.ts` is generated from this array, so adding/removing/changing
 * a row here automatically updates the type used throughout the app.
 *
 * @example
 * // ENUM with a defaultValue → required, typed as the enum's value union
 * { name: 'sort', kind: ENUM, enum: SortEnum, defaultValue: SortEnum.BEST }
 * //   → SearchParameterValues['sort']: SortEnum
 *
 * @example
 * // ENUM without a defaultValue → optional
 * { name: 'mood', kind: ENUM, enum: ColorMoodEnum }
 * //   → SearchParameterValues['mood']?: ColorMoodEnum
 *
 * @example
 * // ENUM_ARRAY → optional, array of the enum's value union
 * { name: 'ethnicity', kind: ENUM_ARRAY, enum: EthnicityEnum }
 * //   → SearchParameterValues['ethnicity']?: EthnicityEnum[]
 *
 * @example
 * // STRING / PHRASE → optional string
 * { name: 'page', kind: STRING }
 * //   → SearchParameterValues['page']?: string
 *
 * @example
 * // STRING_ARRAY → optional string[]
 * { name: 'locations', kind: STRING_ARRAY }
 * //   → SearchParameterValues['locations']?: string[]
 *
 * @example
 * // AGREEMENTS → optional Agreements object
 * { name: 'agreements', kind: AGREEMENTS }
 * //   → SearchParameterValues['agreements']?: Agreements
 */
export const SEARCH_PARAMETERS_SCHEMA = [
  {
    name: 'agreements',
    kind: SearchParameterFieldKind.AGREEMENTS,
  },
  {
    name: 'family',
    kind: SearchParameterFieldKind.ENUM,
    enum: AssetFamilySearchFilterEnum,
    defaultValue: AssetFamilySearchFilterEnum.CREATIVE_AND_EDITORIAL,
  },
  {
    name: 'assettype',
    kind: SearchParameterFieldKind.ENUM,
    enum: AssetTypeSearchFilterEnum,
    defaultValue: AssetTypeSearchFilterEnum.IMAGE_AND_FILM,
    preserveDefault: true,
  },
  {
    name: 'mediatype',
    kind: SearchParameterFieldKind.ENUM,
    enum: MediaTypeSearchFilterEnum,
  },
  {
    name: 'assetfiletype',
    kind: SearchParameterFieldKind.ENUM,
    enum: FileTypeSearchFilterEnum,
  },
  {
    name: 'phrase',
    kind: SearchParameterFieldKind.STRING,
  },
  {
    name: 'events',
    kind: SearchParameterFieldKind.STRING,
  },
  {
    name: 'artistexact',
    kind: SearchParameterFieldKind.STRING,
  },
  {
    name: 'locations',
    kind: SearchParameterFieldKind.STRING_ARRAY,
  },
  {
    name: 'specificpeople',
    kind: SearchParameterFieldKind.STRING_ARRAY,
  },
  {
    name: 'orientations',
    kind: SearchParameterFieldKind.ENUM_ARRAY,
    enum: OrientationEnum,
  },
  {
    name: 'numberofpeople',
    kind: SearchParameterFieldKind.ENUM_ARRAY,
    enum: NumberOfPeopleEnum,
  },
  {
    name: 'ageofpeople',
    kind: SearchParameterFieldKind.ENUM_ARRAY,
    enum: AgeOfPeopleEnum,
  },
  {
    name: 'ethnicity',
    kind: SearchParameterFieldKind.ENUM_ARRAY,
    enum: EthnicityEnum,
  },
  {
    name: 'filmdefinition',
    kind: SearchParameterFieldKind.ENUM_ARRAY,
    enum: FilmDefinitionSearchFilterEnum,
  },
  {
    name: 'videotemplatecategory',
    kind: SearchParameterFieldKind.ENUM_ARRAY,
    enum: VideoTemplateCategoryEnum,
  },
  {
    name: 'videotemplateappversion',
    kind: SearchParameterFieldKind.ENUM_ARRAY,
    enum: VideoTemplateAppVersionEnum,
  },

  {
    name: 'mood',
    kind: SearchParameterFieldKind.ENUM,
    enum: ColorMoodEnum,
  },
  {
    name: 'sort',
    kind: SearchParameterFieldKind.ENUM,
    enum: SortEnum,
    defaultValue: SortEnum.BEST,
    preserveDefault: true,
  },
  {
    name: 'recency',
    kind: SearchParameterFieldKind.ENUM,
    enum: RecencyEnum,
    defaultValue: RecencyEnum.ANY_DATE,
  },
  { name: 'page', kind: SearchParameterFieldKind.NUMBER, defaultValue: 1 },
  {
    name: 'alloweduse',
    kind: SearchParameterFieldKind.ENUM,
    enum: AllowedUseSearchFilterEnum,
  },
  {
    name: 'phraseprocessing',
    kind: SearchParameterFieldKind.ENUM,
    enum: PhraseProcessingSearchFilterEnum,
  },
  {
    name: 'offlinecontent',
    kind: SearchParameterFieldKind.ENUM,
    enum: OfflineContentSearchFilterEnum,
  },
  {
    name: '_colorindex',
    kind: SearchParameterFieldKind.STRING,
    validate: (value) => /^#?[0-9a-fA-F]{3}$|^#?[0-9a-fA-F]{6}$/.test(value),
  },
  { name: 'begindate', kind: SearchParameterFieldKind.STRING },
  {
    name: 'clarifications',
    kind: SearchParameterFieldKind.STRING,
  },
  { name: 'collections', kind: SearchParameterFieldKind.STRING },
  { name: 'color', kind: SearchParameterFieldKind.STRING },
  {
    name: 'compositions',
    kind: SearchParameterFieldKind.ENUM_ARRAY,
    enum: CompositionSearchFilterEnum,
  },
  { name: 'contributor', kind: SearchParameterFieldKind.STRING },
  { name: 'curatedset', kind: SearchParameterFieldKind.STRING },
  { name: 'easyaccess', kind: SearchParameterFieldKind.STRING },
  {
    name: 'editorialproducts',
    kind: SearchParameterFieldKind.ENUM,
    enum: EditorialProductEnum,
    defaultValue: EditorialProductEnum.ALL,
  },
  {
    name: 'editorialsources',
    kind: SearchParameterFieldKind.STRING,
  },
  {
    name: 'editorialsubscription',
    kind: SearchParameterFieldKind.STRING,
  },
  { name: 'embeddable', kind: SearchParameterFieldKind.STRING },
  { name: 'enddate', kind: SearchParameterFieldKind.STRING },
  { name: 'eventid', kind: SearchParameterFieldKind.STRING },
  { name: 'excludenudity', kind: SearchParameterFieldKind.STRING },
  {
    name: 'framerate',
    kind: SearchParameterFieldKind.ENUM_ARRAY,
    enum: FrameRateEnum,
  },
  { name: 'groupbyevent', kind: SearchParameterFieldKind.STRING },
  {
    name: 'imagesize',
    kind: SearchParameterFieldKind.ENUM,
    enum: ImageSizeSearchFilterEnum,
  },
  {
    name: 'imagetechnique',
    kind: SearchParameterFieldKind.ENUM_ARRAY,
    enum: ImageTechniqueEnum,
  },
  { name: 'imageurl', kind: SearchParameterFieldKind.STRING },
  {
    name: 'istockcollection',
    kind: SearchParameterFieldKind.ENUM,
    enum: IstockCollectionSearchFilterEnum,
    defaultValue: IstockCollectionSearchFilterEnum.ALL,
  },
  {
    name: 'istockexclusive',
    kind: SearchParameterFieldKind.ENUM,
    enum: IstockExclusiveSearchFilterEnum,
  },
  {
    name: 'license',
    kind: SearchParameterFieldKind.ENUM,
    enum: LicenseSearchFilterEnum,
  },
  { name: 'morelikethis', kind: SearchParameterFieldKind.STRING },

  { name: 'parenteventid', kind: SearchParameterFieldKind.STRING },
  { name: 'people', kind: SearchParameterFieldKind.STRING },

  { name: 'premiumaccess', kind: SearchParameterFieldKind.STRING },
  {
    name: 'recentlyviewed',
    kind: SearchParameterFieldKind.STRING,
  },
  {
    name: 'releasestatus',
    kind: SearchParameterFieldKind.ENUM,
    enum: ReleaseStatusSearchFilterEnum,
  },
  { name: 'searchbyimage', kind: SearchParameterFieldKind.STRING },
  {
    name: 'specialcollectionid',
    kind: SearchParameterFieldKind.STRING,
  },
  { name: 'stackid', kind: SearchParameterFieldKind.STRING },
  // Used by one site only, but kept unconditional to match existing patterns
  { name: 'subPreview', kind: SearchParameterFieldKind.STRING },
  { name: 'subPreviewSku', kind: SearchParameterFieldKind.STRING },
  {
    name: 'suppressfamilycorrection',
    kind: SearchParameterFieldKind.STRING,
  },
  {
    name: 'suppressphrasecorrection',
    kind: SearchParameterFieldKind.STRING,
  },
  {
    name: 'videodurationmax',
    kind: SearchParameterFieldKind.STRING,
  },
  {
    name: 'videodurationmin',
    kind: SearchParameterFieldKind.STRING,
  },
  {
    name: 'viewpoint',
    kind: SearchParameterFieldKind.ENUM_ARRAY,
    enum: ViewPointEnum,
  },
] as const satisfies readonly SearchParameterSchemaRow[];
